import { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import firstMessageMobileArrowUrl from "@/assets/images/onboarding/mem-agent-first-message-arrow.svg";
import firstMessageMacosImageUrl from "@/assets/images/onboarding/mem-agent-first-message-macos.png";
import firstMessageWindowsImageUrl from "@/assets/images/onboarding/mem-agent-first-message.png";
import type { StepRenderProps } from "@/components/onboarding/components/welcome-wizard";
import { WizardContentWrapper } from "@/components/onboarding/components/welcome-wizard";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import { clientEnvModule } from "@/modules/client-env";
import {
  getDefaultMemAgentHoldToTalkKey,
  getMemAgentHoldToTalkKeyDisplay,
  type MemAgentHoldToTalkKey,
} from "@/modules/mem-agent/hold-to-talk-key";
import { prepareFloatingMemForOnboarding } from "@/modules/mem-agent/prepare-floating-mem-for-onboarding";
import { useAppStore } from "@/store";

const SMALL_WEB_MEDIA_QUERY = "@media (max-width: 600px)";

/**
 * Prepares the floating agent for first-message onboarding.
 *
 * The overlay remains locked until the floating-agent page reports that the
 * user opened the panel, confirmed a voice hold, or sent a message; this hook
 * only seeds the prompt and enables the widget.
 */
export const usePrepareMemAgentFirstMessageOnboarding = () => {
  const { store } = useAppStore();
  const hasEnteredStepRef = useRef(false);

  useEffect(() => {
    if (hasEnteredStepRef.current) return;
    hasEnteredStepRef.current = true;

    void (async () => {
      const preparedFloatingMem = await prepareFloatingMemForOnboarding({ store });
      if (preparedFloatingMem) {
        store.floatingAgent.setUserEnabled(true);
      }
    })();
  }, [store]);
};

export const MemAgentFirstMessageStep = observer((_props: StepRenderProps) => {
  usePrepareMemAgentFirstMessageOnboarding();
  const { store } = useAppStore();
  const holdToTalkKey = store.spaceAccountSettings.memAgent.holdToTalkKey;

  return (
    <MemAgentFirstMessageStepView
      holdToTalkKey={holdToTalkKey ?? undefined}
      isDesktop={clientEnvModule.isDesktop()}
    />
  );
});

export interface MemAgentFirstMessageStepViewProps {
  holdToTalkKey?: MemAgentHoldToTalkKey;
  isWindows?: boolean;
  isDesktop?: boolean;
}

const getFirstMessageMedia = ({ isWindows }: { isWindows?: boolean }) => {
  const shouldShowWindowsImage = isWindows ?? clientEnvModule.isWindows();

  if (shouldShowWindowsImage) {
    return { kind: "windows", url: firstMessageWindowsImageUrl } as const;
  }

  return {
    kind: "macos",
    url: firstMessageMacosImageUrl,
  } as const;
};

/** Presents the final Mem Agent onboarding step that points users toward the floating agent. */
export const MemAgentFirstMessageStepView = ({
  holdToTalkKey,
  isDesktop,
  isWindows = false,
}: MemAgentFirstMessageStepViewProps) => {
  const media = getFirstMessageMedia({ isWindows });

  if (!isDesktop) {
    return (
      <FirstMessageWebStepWrapper>
        <FirstMessageLargeWebStepWrapper data-test-id="mem-agent-first-message-large-web">
          <FirstMessageTitle
            variant={MDSTextVariant.HeaderL}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Contrast}
          >
            Send your first message to Mem Agent to continue
          </FirstMessageTitle>

          <FirstMessageSlot>
            <FirstMessageMedia media={media} />
            <FirstMessageBody>
              <FirstMessageCopy variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
                Click on the floating Mem Agent icon and introduce yourself.
              </FirstMessageCopy>
            </FirstMessageBody>
          </FirstMessageSlot>
        </FirstMessageLargeWebStepWrapper>

        <FirstMessageSmallWebStepWrapper data-test-id="mem-agent-first-message-small-web">
          <FirstMessageTitle
            variant={MDSTextVariant.HeaderM}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Contrast}
          >
            Send your first message to Mem Agent to continue
          </FirstMessageTitle>

          <FirstMessageMobileSlot>
            <FirstMessageMobileBody>
              <FirstMessageCopy variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
                Click on the floating Mem Agent icon and introduce yourself.
              </FirstMessageCopy>
              <FirstMessageMobileArrowFrame aria-hidden="true">
                <FirstMessageMobileArrow
                  src={firstMessageMobileArrowUrl}
                  alt=""
                  data-test-id="mem-agent-first-message-mobile-arrow"
                />
              </FirstMessageMobileArrowFrame>
            </FirstMessageMobileBody>
          </FirstMessageMobileSlot>
        </FirstMessageSmallWebStepWrapper>
      </FirstMessageWebStepWrapper>
    );
  }

  const displayHoldToTalkKey = holdToTalkKey ?? getDefaultMemAgentHoldToTalkKey({ isWindows });
  const holdToTalkDisplay = getMemAgentHoldToTalkKeyDisplay(displayHoldToTalkKey, { isWindows });

  return (
    <FirstMessageStepWrapper>
      <FirstMessageTitle
        variant={MDSTextVariant.HeaderL}
        weight={MDSTextWeight.SemiBold}
        color={MDSTextColor.Contrast}
      >
        Send your first message to Mem Agent to continue
      </FirstMessageTitle>

      <FirstMessageSlot>
        <FirstMessageMedia media={media} />
        <FirstMessageBody>
          <FirstMessageCopy variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
            Press and hold {holdToTalkDisplay.accessibleLabel} while talking to introduce yourself.
          </FirstMessageCopy>
          <FirstMessageCopy variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
            If you can&apos;t talk out loud right now, click on the floating Mem Agent icon.
          </FirstMessageCopy>
        </FirstMessageBody>
      </FirstMessageSlot>
    </FirstMessageStepWrapper>
  );
};

const FirstMessageMedia = ({ media }: { media: ReturnType<typeof getFirstMessageMedia> }) => {
  if (media.kind === "windows") {
    return (
      <FirstMessageWindowsImageFrame>
        <FirstMessageWindowsImage src={media.url} alt="Mem Agent message preview" />
      </FirstMessageWindowsImageFrame>
    );
  }

  return (
    <FirstMessageImageFrame>
      <FirstMessageMacosImage src={media.url} alt="Mem Agent message preview" />
    </FirstMessageImageFrame>
  );
};

const FirstMessageStepWrapper = styled(WizardContentWrapper)`
  gap: 0;
`;

const FirstMessageWebStepWrapper = styled(WizardContentWrapper)`
  gap: 0;
`;

const FirstMessageLargeWebStepWrapper = styled(FirstMessageStepWrapper)`
  ${SMALL_WEB_MEDIA_QUERY} {
    display: none;
  }
`;

const FirstMessageSmallWebStepWrapper = styled(FirstMessageStepWrapper)`
  display: none;
  align-items: flex-start;

  ${SMALL_WEB_MEDIA_QUERY} {
    display: flex;
  }
`;

const FirstMessageBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;
`;

const FirstMessageMobileBody = styled(FirstMessageBody)`
  gap: 0;
`;

const FirstMessageTitle = styled(MDSText)`
  display: block;
  padding-bottom: ${({ theme }) => theme.spacing[2]};
  word-break: break-word;
`;

const FirstMessageSlot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;

const FirstMessageMobileSlot = styled(FirstMessageSlot)`
  gap: 0;
  justify-content: center;
  width: 100%;
`;

const FirstMessageImageFrame = styled.div`
  display: flex;
  width: 416px;
  max-width: 100%;
  aspect-ratio: 416 / 189;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background: ${({ theme }) => theme.colors.surface.modal};
`;

const FirstMessageMacosImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FirstMessageWindowsImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FirstMessageWindowsImageFrame = styled(FirstMessageImageFrame)`
  width: 474px;
  aspect-ratio: 474 / 265;
`;

const FirstMessageMobileArrowFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  width: 40px;
  height: 48px;
`;

const FirstMessageMobileArrow = styled.img`
  display: block;
  width: 22px;
  height: 43px;
`;

const FirstMessageCopy = styled(MDSText)`
  display: block;
  width: 100%;
`;
